import { useLoad } from "@allergy123/api-client/react";
import { Card, Status, Table, Td, Th } from "@allergy123/ui";

const money = (cents: number, currency: string) => new Intl.NumberFormat(undefined, { style: "currency", currency }).format(cents / 100);

/** Issued commission statements. Lines are plan-agnostic until the commission plan is approved (ADR 0012). */
export function StatementsPage() {
  const data = useLoad((a) => a.reps.statements());
  if (data.state === "error") return <Status kind="error" label={`Couldn't load statements (${data.error?.message})`} />;
  if (!data.data) return <p className="text-ink-secondary">Loading…</p>;
  const { statements } = data.data;
  return (
    <>
      <h1 className="mb-2 text-h1 font-semibold text-anchor">Commission statements</h1>
      <p className="mb-8 max-w-[68ch] text-ink-secondary">One statement per period. An issued statement doesn't change; a correction is a new statement that references it.</p>
      {statements.length === 0 && (
        <Card>
          <p className="text-ink-secondary">No statements issued yet.</p>
        </Card>
      )}
      <div className="grid gap-6">
        {statements.map((s) => (
          <Card key={s.id} as="section">
            <div className="mb-4 flex flex-col gap-1 sm:flex-row sm:items-baseline sm:justify-between">
              <h2 className="text-h3 font-semibold text-anchor">
                {s.periodStart} to {s.periodEnd}
              </h2>
              <span className="text-small text-ink-secondary">
                {s.status}
                {s.issuedAt ? ` · issued ${new Date(s.issuedAt).toLocaleDateString()}` : ""}
                {s.planVersion ? ` · plan ${s.planVersion}` : " · plan pending approval"}
              </span>
            </div>
            <Table>
              <thead>
                <tr>
                  <Th>Line</Th>
                  <Th className="text-right">Amount</Th>
                </tr>
              </thead>
              <tbody>
                {s.lines.map((l, i) => (
                  <tr key={i}>
                    <Td>{l.description}</Td>
                    <Td className="text-right tabular-nums">{money(l.amountCents, s.currency)}</Td>
                  </tr>
                ))}
                <tr>
                  <Td className="font-semibold">Total</Td>
                  <Td className="text-right font-semibold tabular-nums">{money(s.totalCents, s.currency)}</Td>
                </tr>
              </tbody>
            </Table>
          </Card>
        ))}
      </div>
    </>
  );
}
